import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { AuthNoticeService, UserService } from '../../../../core/_services';
import { AuthService } from '../auth.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable } from 'rxjs';

@Component({
    selector: 'kt-new-login',
    templateUrl: './new-login.component.html',
    styleUrls: ['./new-login.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NewLoginComponent implements OnInit, OnDestroy {
    loginForm: FormGroup;
    loading = false;
    isMounted = false;
    showPassword = false;
    isLoggedIn$: Observable<boolean>;
    errors: any = [];

    private returnUrl: any;

    constructor(
        private router: Router,
        private userService: UserService,
        private authNoticeService: AuthNoticeService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private permissionsService: NgxPermissionsService,
        private authService: AuthService
    ) {
        // synchronously check for SSO parameters before clearing localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const hasSso = (urlParams.has('access_token') && urlParams.has('refresh_token')) ||
                       (this.route.snapshot && this.route.snapshot.queryParams && this.route.snapshot.queryParams.access_token && this.route.snapshot.queryParams.refresh_token);
        const isLoggedIn = this.permissionsService.getPermissions().LOGGED_IN;
        if (!hasSso && !isLoggedIn) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
            this.permissionsService.flushPermissions();
        }
    }

    ngOnInit(): void {
        this.initLoginForm();
        
        this.route.queryParams.subscribe(params => {
            this.returnUrl = params.returnUrl || '/';
            const accessToken = params.access_token;
            const refreshToken = params.refresh_token;
            if (accessToken && refreshToken) {
                this.loading = true;
                localStorage.setItem('accessToken', accessToken);
                this.authService.setTokens({ IdToken: accessToken, RefreshToken: refreshToken });
                localStorage.setItem('refreshToken', refreshToken);
                
                // Clean up tokens from URL history to prevent header field too large error
                const urlWithoutParams = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, urlWithoutParams);
                
                this.hydrateUserContext(accessToken);
            } else if (this.permissionsService.getPermissions().LOGGED_IN) {
                this.router.navigateByUrl(this.returnUrl || '/');
            }
        });

        setTimeout(() => {
            this.isMounted = true;
            this.cdr.detectChanges();
        }, 100);
    }

    ngOnDestroy(): void {
        this.authNoticeService.setNotice(null, 'info');
    }

    initLoginForm() {
        this.loginForm = this.fb.group({
            clientUid: [''],
            email: ['', Validators.compose([
                Validators.required,
                Validators.email,
            ])],
            password: ['', Validators.compose([
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100)
            ])]
        });
    }

    hydrateUserContext(token: string) {
        this.loading = true;
        this.authService.fetchUserContext(token).subscribe(
            meData => {
                this.loading = false;
                const role = meData.role || (meData.roles && meData.roles[0]) || 'USER';
                const userEmail = meData.email;
                
                const lenderData = {
                    type: meData.tenant_type || "",
                    org: meData.tenant_id || "",
                    role: role,
                    name: meData.name || userEmail.split("@")[0],
                    email: userEmail
                };
                
                localStorage.setItem('email_id_raindow', userEmail);
                localStorage.setItem('user', JSON.stringify(lenderData));
                this.authService.user = {
                    email: userEmail,
                    userName: userEmail,
                    role: role,
                    name: meData.name || userEmail.split("@")[0],
                    ...meData
                };
                
                const permissions = this.authService.mapPermissionsToLegacy(meData.permissions_flat || []);
                this.authService.permissions = permissions;
                localStorage.setItem('permissions', JSON.stringify(permissions));
                this.permissionsService.loadPermissions(permissions);
                this.permissionsService.addPermission('LOGGED_IN');
                
                this.router.navigateByUrl(this.returnUrl || '/');
                this.cdr.detectChanges();
            },
            error => {
                console.error("Failed to fetch user context:", error);
                this.loading = false;
                this.authNoticeService.setNotice('Could not retrieve user information from Finbase Auth.', 'danger');
                localStorage.clear();
                this.permissionsService.flushPermissions();
                this.cdr.detectChanges();
            }
        );
    }

    submit() {
        const controls = this.loginForm.controls;

        if (this.loginForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );
            return;
        }

        this.loading = true;
        this.authNoticeService.setNotice(null, 'info');

        const authData = {
            email: controls.email.value,
            password: controls.password.value,
            client_uid: controls.clientUid.value || ''
        };

        this.userService.login(authData).subscribe(
            data => {
                if (data && data.access && data.refresh) {
                    localStorage.setItem('accessToken', data.access);
                    this.authService.setTokens({ IdToken: data.access, RefreshToken: data.refresh });
                    localStorage.setItem('refreshToken', data.refresh);
                    
                    this.hydrateUserContext(data.access);
                } else {
                    this.loading = false;
                    this.authNoticeService.setNotice('Invalid response from Finbase Auth.', 'danger');
                    this.cdr.detectChanges();
                }
            },
            error => {
                const msg = (error && error.error && error.error.detail) || 
                            (error && error.error && error.error.message) || 
                            (error && error.error && error.error.error) || 
                            'Invalid credentials. Please try again.';
                this.authNoticeService.setNotice(msg, 'danger');
                this.loading = false;
                this.cdr.detectChanges();
            }
        );
    }

    togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }

    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.loginForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result = control.hasError(validationType) && (control.dirty || control.touched);
        return result;
    }
}